import { AxiosError, AxiosResponse } from 'axios'

import { ScanCache } from './ScanCache'
import { ScanLog } from './ScanLog'
import { Action, Handler, PreviewHandler } from './handlers'
import { skuit } from '../services/skuit'
import { ScanResult } from '../services/client/Lookup'
import { Context, Generic, makeGeneric } from '../api/models'

export enum Event {
  Loading,
  Reset,
  Action,
}

interface CbProps {
  event: Event
  data?: any
}

export type ScanContextCallback = (props: CbProps) => void

type LocalData = any

const commandMap: { [key: string]: (scanContext: ScanContext, data?: any) => Handler | undefined} = {
  found: (scanContext: ScanContext, data?: any) => scanContext.handler?.applyFound(data),
  audit: (scanContext: ScanContext, data?: any) =>
    scanContext.handler?.applyAudit(data),
  scan: (scanContext: ScanContext, data?: any) => {
    const scanned = makeGeneric(data.scanned)
    scanContext.add(scanned, new Context())
    return scanContext.handler
  },
  print: (scanContext: ScanContext, data?: any) =>
    scanContext.handler?.applyPrint(data),
  printCaseLabels: (scanContext: ScanContext, data?: any) =>
    scanContext.handler?.applyPrintCaseLabels(data),
  setSSCC: (scanContext: ScanContext, data?: any) =>
    scanContext.handler?.applySetSSCC(data),
}

export class ScanContext {
  cache: ScanCache
  scanLog: ScanLog
  handler?: Handler
  callbacks: ScanContextCallback[]

  constructor(scanLog?: ScanLog) {
    this.cache = new ScanCache()
    this.scanLog = new ScanLog()
    this.callbacks = []
    if (scanLog) {
      // replay scans
      scanLog.scans.forEach(({ ident }) => this.scan(ident))
    }
  }

  reset = () => {
    this.cache = new ScanCache()
    this.handler = undefined
    this.scanLog = new ScanLog()
    this.notify({ event: Event.Reset })
  }

  scan = (ident: string) => {
    this.notify({ event: Event.Loading, data: { loading: true } })
    const unpacked = ident.split('|')
    let localData: any
    if (unpacked.length > 1) {
      try {
        localData = JSON.parse(unpacked[1])
      } catch {}
    }
    const cached = this.cache.get(ident)
    if (cached) {
      this.scanLog.log(ident, undefined)
      if (cached[0]) {
        this.add(cached[0].scanned, cached[0].context, localData)
      }
      this.notify({ event: Event.Loading, data: { loading: false } })
    } else {
      const context = this.handler?.getContext()
      const params = { scan: ident, ...context }
      skuit.sc.lookup
        .ident({ params })
        .then((result: [ScanResult, AxiosResponse]) => {
          const [obj, response] = result
          this.add(obj.scanned, obj.context, localData)
          this.scanLog.log(ident, response)
          this.cache.store(ident, result)
        })
        .catch((error: AxiosError) => {
          this.scanLog.log(ident, error.response)
        })
        .finally(() => {
          this.notify({ event: Event.Loading, data: { loading: false } })
        })
    }
  }

  command = (command: string, data?: any) => {
    const commandFunc = commandMap[command]
    if (command) {
      this.handler = commandFunc(this, data)
    }
    this.notify({ event: Event.Loading, data: { loading: false } })
    this.notify({ event: Event.Reset })
  }

  add = (scanned: Generic, context: Context, local?: LocalData) => {
    if (!this.handler) {
      this.handler = new PreviewHandler({ cache: this.cache })
    }
    const [action, data] = this.handler.apply({ scanned, context, local })
    if (action === Action.ChangeHandler) {
      // data should be a Handler type
      this.handler = data
      return [Action.None, null]
    }
    if (action !== Action.None) {
      this.notify({ event: Event.Action, data: { action, data } })
    }
    return [action, data]
  }

  registerCb = (cb: ScanContextCallback) => {
    this.callbacks.push(cb)
  }

  removeCb = (cb: ScanContextCallback) => {
    const index = this.callbacks.indexOf(cb)
    if (index > -1) {
      this.callbacks.splice(index, 1)
    }
  }

  notify = (props: CbProps) => {
    this.callbacks.map((cb) => cb(props))
  }
}
